import { fromZonedTime } from "date-fns-tz";

/** RFC-3339 UTC, truncated to seconds, trailing Z. */
export function nowIso(date: Date): string {
  return `${date.toISOString().slice(0, 19)}Z`;
}

export interface UtcInterval {
  startMs: number;
  endMs: number;
}

/**
 * A person's working window (local HH:MM in their IANA tz) projected onto a given
 * UTC calendar day. Handles DST because fromZonedTime resolves the wall-clock time
 * in the zone. Overnight windows (end <= start) wrap to the next day.
 */
export function workingIntervalUtc(
  dayIsoDate: string, // YYYY-MM-DD
  workingStart: string, // HH:MM
  workingEnd: string,
  timezone: string,
): UtcInterval {
  const startMs = fromZonedTime(`${dayIsoDate}T${workingStart}:00`, timezone).getTime();
  let endMs = fromZonedTime(`${dayIsoDate}T${workingEnd}:00`, timezone).getTime();
  if (endMs <= startMs) endMs += 24 * 60 * 60 * 1000; // overnight
  return { startMs, endMs };
}

/** Intersection of N intervals; null if any pair is disjoint. */
export function intersectIntervals(intervals: UtcInterval[]): UtcInterval | null {
  const [first, ...rest] = intervals;
  if (!first) return null;
  let start = first.startMs;
  let end = first.endMs;
  for (const iv of rest) {
    start = Math.max(start, iv.startMs);
    end = Math.min(end, iv.endMs);
    if (start >= end) return null;
  }
  return { startMs: start, endMs: end };
}

/**
 * Is a travel claim active on `asOf` (YYYY-MM-DD)? date_to NULL = ongoing.
 * Inclusive of both ends.
 */
export function isActiveOn(asOf: string, dateFrom: string, dateTo: string | null): boolean {
  if (asOf < dateFrom) return false;
  if (dateTo === null) return true;
  return asOf <= dateTo;
}
